import { useGlobalContext } from "context/GlobalContext";
import { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { message } from "constants/responses/message";
import { taskCompletionStatusApi } from "API/apis/dashboard/taskCompletionStatus";
import { dataFetchFrequency } from "constants/componentsConstants";
import { useParams } from "react-router-dom";
import { taskCompletionStatusViewerApi } from "API/apis/viewer/taskCompletionStatusViewer";

const initialValue = {
  pending: {
    records: [],
    recordsCount: 0,
  },
  accepted: {
    records: [],
    recordsCount: 0,
  },
};

const TaskDashboardContext = createContext({
  pendingRecordCount: 0,
  setPendingRecordCount: () => {},
  acceptedRecordCount: 0,
  setAcceptedRecordCount: () => {},
  pendingRecord: [],
  setPendingRecord: () => {},
  acceptedRecords: [],
  setAcceptedRecords: () => {},
  reloadRequired: false,
  setReloadRequired: () => {},
});

export const TaskDashboardProvider = ({ children }) => {
  const [pendingRecordCount, setPendingRecordCount] = useState(
    initialValue.pending.recordsCount
  );
  const [acceptedRecordCount, setAcceptedRecordCount] = useState(
    initialValue.accepted.recordsCount
  );
  const [pendingRecord, setPendingRecord] = useState(
    initialValue.pending.records
  );
  const [acceptedRecords, setAcceptedRecords] = useState(
    initialValue.accepted.records
  );
  const [reloadRequired, setReloadRequired] = useState(false);
  const { setIsInitializing, setError, setIsLoading } = useGlobalContext();
  const { isViewer } = useSelector((state) => state.auth);

  const { projectId } = useParams();

  useEffect(() => {
    setIsInitializing(true);
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (isViewer) {
          const { data, status } = await taskCompletionStatusViewerApi(
            isViewer,
            projectId ? [4, 5] : [1, 2, 3]
          );
          if (status !== 200) {
            throw new Error(message.error.generalFailure);
          }
          setPendingRecordCount(data.pending.recordsCount);
          setAcceptedRecordCount(data.accepted.recordsCount);
          setPendingRecord(data.pending.records);
          setAcceptedRecords(data.accepted.records);
        } else {
          const { data, status } = await taskCompletionStatusApi();
          if (status !== 200) {
            throw new Error(message.error.generalFailure);
          }
          setPendingRecordCount(data.pending.recordsCount);
          setAcceptedRecordCount(data.accepted.recordsCount);
          setPendingRecord(data.pending.records);
          setAcceptedRecords(data.accepted.records);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
        setIsInitializing(false);
        setReloadRequired(false);
      }
    };
    fetchData();
    const intervalId = setInterval(() => {
      fetchData();
    }, dataFetchFrequency.taskDashboard);
    return () => clearInterval(intervalId);
  }, [reloadRequired]);

  return (
    <TaskDashboardContext.Provider
      value={{
        pendingRecordCount,
        setPendingRecordCount,
        acceptedRecordCount,
        setAcceptedRecordCount,
        pendingRecord,
        setPendingRecord,
        acceptedRecords,
        setAcceptedRecords,
        reloadRequired,
        setReloadRequired,
      }}
    >
      <>{children}</>
    </TaskDashboardContext.Provider>
  );
};

export const useTaskDashboardContext = () => {
  const context = useContext(TaskDashboardContext);
  if (!context) {
    throw new Error(
      "useTaskDashboardContext must be used within a TaskDashboardProvider"
    );
  }
  return context;
};
